import { MessageLog } from "@common/models/message-log";
import { TimelineItem } from "./TimelineItem";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import moment from "moment";
import { formatDelta } from "@common/utils/time-format-utils";
import { AlertTriangle, CheckCircle, XCircle, Clock, Server, Smartphone, AlertCircle, Info } from "lucide-react";

interface MessageLogTimelineProps {
    logs: MessageLog[][];  // Array of log groups
    messageId?: string;
    dueDate?: string | Date;
}

interface ProblemSummary {
    hasProblems: boolean;
    hasErrorLogs: boolean;
    multipleGateways: { versions: string[]; count: number } | null;
    duplicateSendAttempts: number;
    storeCheckFailures: number;
    retryRejections: number;
    totalRetries: number;
    finalStatus: 'success' | 'failed' | 'pending' | 'unknown';
    totalAttempts: number;
    sentCount: number;
}

/**
 * Build a map of log IDs to their send number (for "sent successfully" logs)
 * Returns map: logId -> { sendNumber, totalSends, isDuplicate }
 */
function buildSendNumberMap(logs: MessageLog[][]): Map<string, { sendNumber: number; totalSends: number; isDuplicate: boolean }> {
    const allLogs = logs.flat();
    const sendMap = new Map<string, { sendNumber: number; totalSends: number; isDuplicate: boolean }>();

    // Sort by createdAt to get chronological order
    const sortedLogs = [...allLogs].sort((a, b) =>
        new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
    );

    // Find all "sent successfully" logs
    let sendNumber = 0;
    const sendLogIds: string[] = [];
    sortedLogs.forEach(log => {
        const msg = log.metadata?.logMessage || '';
        if (msg.includes('sent successfully')) {
            sendNumber++;
            sendLogIds.push(log._id);
            sendMap.set(log._id, {
                sendNumber,
                totalSends: 0, // Will be updated after counting all
                isDuplicate: sendNumber > 1
            });
        }
    });

    // Update totalSends for all entries
    sendMap.forEach((value, key) => {
        value.totalSends = sendNumber;
    });

    return sendMap;
}

/**
 * Analyze logs to find problems
 */
function analyzeProblems(logs: MessageLog[][]): ProblemSummary {
    const allLogs = logs.flat();

    // Extract unique WhatsApp versions
    const waVersions = new Set<string>();
    allLogs.forEach(log => {
        if (log.metadata?.waVersion) {
            waVersions.add(log.metadata.waVersion);
        }
    });

    // Count problem patterns
    let duplicateSendAttempts = 0;
    let storeCheckFailures = 0;
    let retryRejections = 0;
    let totalRetries = 0;
    let sentCount = 0;

    allLogs.forEach(log => {
        const msg = log.metadata?.logMessage || '';

        // Count "sent successfully" logs
        if (msg.includes('sent successfully')) {
            sentCount++;
        }

        // Count store check failures that lead to duplicate sends
        if (msg.includes('Message not found in store') && msg.includes('will proceed')) {
            duplicateSendAttempts++;
        }

        // Count investigation store failures
        if (msg.includes('not found in store') && msg.includes('checked')) {
            storeCheckFailures++;
        }

        // Count retry rejections
        if (msg.includes('Retry rejected')) {
            retryRejections++;
        }

        // Count retry triggers
        if (msg.includes('Retry triggered') || msg.includes('retry attempt')) {
            totalRetries++;
        }
    });

    // Check if any log has an error status or error field
    const hasErrorLogs = allLogs.some(log => {
        const status = log.status?.toLowerCase() || '';
        const logMessage = log.metadata?.logMessage?.toLowerCase() || '';
        return status.includes('failed') || status.includes('error') ||
            logMessage.includes('failed') || logMessage.includes('error') ||
            !!log.error;
    });

    // Determine final status
    const lastLog = allLogs[allLogs.length - 1];
    let finalStatus: 'success' | 'failed' | 'pending' | 'unknown' = 'unknown';
    if (lastLog) {
        const status = lastLog.status?.toLowerCase() || '';
        if (status.includes('confirmed') || status.includes('received')) {
            finalStatus = 'success';
        } else if (status.includes('failed') || hasErrorLogs) {
            finalStatus = 'failed';
        } else if (status.includes('pending') || status.includes('before') || status.includes('bt-sent')) {
            finalStatus = 'pending';
        }
    }

    const multipleGateways = waVersions.size > 1
        ? { versions: Array.from(waVersions), count: waVersions.size }
        : null;

    const hasProblems = multipleGateways !== null || duplicateSendAttempts > 1 || sentCount > 1 || hasErrorLogs;

    return {
        hasProblems,
        hasErrorLogs,
        multipleGateways,
        duplicateSendAttempts,
        storeCheckFailures,
        retryRejections,
        totalRetries,
        finalStatus,
        totalAttempts: allLogs.filter(l => l.metadata?.logMessage?.includes('Preparing')).length,
        sentCount
    };
}

// formatDelta is now imported from @common/utils/time-format-utils

/**
 * Format group metadata for display
 */
function formatGroupMetadata(group: MessageLog[], previousGroupLastLog?: MessageLog): { timeRange?: string; duration?: string; eventCount: number; deltaFromPrevious?: string } {
    if (group.length === 0) {
        return { eventCount: 0 };
    }

    const firstLog = group[0];
    const lastLog = group[group.length - 1];

    const firstTime = moment(firstLog.createdAt);
    const lastTime = moment(lastLog.createdAt);

    const timeRange = group.length > 1
        ? `${firstTime.format('h:mm:ss A')} - ${lastTime.format('h:mm:ss A')}`
        : firstTime.format('h:mm:ss A');

    const durationMs = lastTime.valueOf() - firstTime.valueOf();
    const duration = durationMs > 0
        ? moment.duration(durationMs).humanize()
        : undefined;

    const deltaFromPrevious = previousGroupLastLog && previousGroupLastLog.createdAt && firstLog.createdAt
        ? formatDelta(previousGroupLastLog.createdAt, firstLog.createdAt)
        : undefined;

    return {
        timeRange,
        duration,
        eventCount: group.length,
        deltaFromPrevious
    };
}

/**
 * Check if a log entry indicates a problem
 */
function isProblematicLog(log: MessageLog): 'warning' | 'error' | 'info' | null {
    const msg = log.metadata?.logMessage || '';

    // Error patterns (use String() to handle runtime 'failed' status not in type)
    const statusStr = String(log.status);
    if (msg.includes('failed') || msg.includes('error') || statusStr === 'failed') {
        return 'error';
    }

    // Warning patterns - things that indicate duplicate send risk
    if (msg.includes('Message not found in store') && msg.includes('will proceed')) {
        return 'warning';
    }
    if (msg.includes('Retry rejected')) {
        return 'info';
    }

    return null;
}

export function MessageLogTimeline({ logs, messageId, dueDate }: MessageLogTimelineProps) {
    const isFlatArray = logs && logs.length > 0 && !Array.isArray(logs[0]);

    if (!logs || logs.length === 0) {
        return (
            <Card className="border-0 shadow-none bg-transparent">
                <CardContent className="py-4">
                    <p className="text-sm text-slate-500 dark:text-slate-400">No log entries available</p>
                </CardContent>
            </Card>
        );
    }

    let logGroups: MessageLog[][] = isFlatArray ? [logs as any as MessageLog[]] : logs as MessageLog[][];

    // Sort groups chronologically
    logGroups = [...logGroups].sort((groupA, groupB) => {
        if (groupA.length === 0 && groupB.length === 0) return 0;
        if (groupA.length === 0) return 1;
        if (groupB.length === 0) return -1;

        const timeA = moment(groupA[0].createdAt).valueOf();
        const timeB = moment(groupB[0].createdAt).valueOf();
        return timeA - timeB;
    });

    // Build send number map for highlighting successful sends
    const sendNumberMap = buildSendNumberMap(logGroups);

    // Analyze for problems
    const problems = analyzeProblems(logGroups);
    const totalLogs = logGroups.flat().length;

    return (
        <div className="space-y-4">
            {/* Problem Summary Header — borderless, the bg tint alone carries the status colour */}
            <Card className={`border-0 shadow-none ${problems.finalStatus === 'failed' ? 'bg-red-50 dark:bg-red-900/20' : problems.hasProblems ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                        {/* Status Icon */}
                        <div className={`p-2 rounded-full ${problems.finalStatus === 'failed' ? 'bg-red-100 dark:bg-red-800' : problems.hasProblems ? 'bg-orange-100 dark:bg-orange-800' : 'bg-green-100 dark:bg-green-800'}`}>
                            {problems.finalStatus === 'failed' ? (
                                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                            ) : problems.hasProblems ? (
                                <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                            ) : problems.finalStatus === 'success' ? (
                                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                            ) : (
                                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            )}
                        </div>

                        {/* Summary Content */}
                        <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">
                                {problems.finalStatus === 'failed' ? 'Message Send Failed' : problems.hasProblems ? 'Issues Detected' : 'Message Delivery Summary'}
                            </h3>

                            {/* Quick Stats */}
                            <div className="flex flex-wrap gap-3 mb-3">
                                <Badge variant="outline" className="bg-white dark:bg-slate-800">
                                    <Info className="h-3 w-3 mr-1" />
                                    {totalLogs} total logs
                                </Badge>
                                {problems.sentCount > 0 && (
                                    <Badge
                                        variant="outline"
                                        className={problems.sentCount > 1 ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300' : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300'}
                                    >
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        {problems.sentCount} send{problems.sentCount !== 1 ? 's' : ''} recorded
                                        {problems.sentCount > 1 && ' ⚠️ DUPLICATE!'}
                                    </Badge>
                                )}
                                {problems.totalRetries > 0 && (
                                    <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300">
                                        {problems.totalRetries} retry triggers
                                    </Badge>
                                )}
                                {problems.retryRejections > 0 && (
                                    <Badge variant="outline" className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                        {problems.retryRejections} retries blocked (good)
                                    </Badge>
                                )}
                            </div>

                            {/* Problem Details */}
                            {problems.hasProblems && (
                                <div className="space-y-2">
                                    {problems.multipleGateways && (
                                        <Alert variant="destructive" className="py-2">
                                            <Server className="h-4 w-4" />
                                            <AlertTitle className="text-sm">Multiple Gateway Instances</AlertTitle>
                                            <AlertDescription className="text-xs">
                                                {problems.multipleGateways.count} different WhatsApp versions detected:
                                                {problems.multipleGateways.versions.map((v, i) => (
                                                    <Badge key={v} variant="outline" className="ml-1 text-xs">v{v}</Badge>
                                                ))}
                                                <br />
                                                <strong>This causes duplicate sends!</strong> Check the Sessions tab.
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {problems.sentCount > 1 && (
                                        <Alert variant="destructive" className="py-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertTitle className="text-sm">Duplicate Sends Detected!</AlertTitle>
                                            <AlertDescription className="text-xs">
                                                This message was sent {problems.sentCount} times.
                                                The recipient likely received duplicate messages.
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {problems.duplicateSendAttempts > 1 && problems.sentCount <= 1 && (
                                        <Alert className="py-2 border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20">
                                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                            <AlertTitle className="text-sm text-yellow-800 dark:text-yellow-300">Multiple Send Attempts</AlertTitle>
                                            <AlertDescription className="text-xs text-yellow-700 dark:text-yellow-400">
                                                {problems.duplicateSendAttempts} clients attempted to send after "not found in store".
                                                The deduplication logic prevented actual duplicates.
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {problems.hasErrorLogs && (
                                        <Alert variant="destructive" className="py-2">
                                            <XCircle className="h-4 w-4" />
                                            <AlertTitle className="text-sm">Send Error</AlertTitle>
                                            <AlertDescription className="text-xs">
                                                An error occurred while sending this message. Check the timeline below for details.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </div>
                            )}

                            {!problems.hasProblems && problems.finalStatus === 'success' && (
                                <p className="text-sm text-green-700 dark:text-green-300">
                                    Message was delivered successfully without issues.
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Timeline Groups */}
            {logGroups.map((group, groupIndex) => {
                const previousGroupLastLog = groupIndex > 0 && logGroups[groupIndex - 1].length > 0
                    ? logGroups[groupIndex - 1][logGroups[groupIndex - 1].length - 1]
                    : undefined;

                const metadata = formatGroupMetadata(group, previousGroupLastLog);

                // Check if this group has any problematic logs
                const groupHasWarnings = group.some(log => isProblematicLog(log) === 'warning');
                const groupHasErrors = group.some(log => isProblematicLog(log) === 'error');

                return (
                    <Card
                        key={`group-${groupIndex}`}
                        className={`border-0 shadow-none ${
                            groupHasErrors
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : groupHasWarnings
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                    : 'bg-slate-50 dark:bg-slate-800/50'
                        }`}
                    >
                        <CardContent className="p-4">
                            {/* Group header */}
                            <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    {metadata.timeRange && (
                                        <span className="font-medium">{metadata.timeRange}</span>
                                    )}
                                    {metadata.duration && (
                                        <>
                                            <span className="text-slate-400 dark:text-slate-500">•</span>
                                            <span>{metadata.duration}</span>
                                        </>
                                    )}
                                    <span className="text-slate-400 dark:text-slate-500">•</span>
                                    <span>{metadata.eventCount} event{metadata.eventCount !== 1 ? 's' : ''}</span>
                                    {metadata.deltaFromPrevious && groupIndex > 0 && (
                                        <>
                                            <span className="text-slate-400 dark:text-slate-500">•</span>
                                            <Badge
                                                variant="outline"
                                                className="text-xs font-normal bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 px-2 py-0.5"
                                            >
                                                {metadata.deltaFromPrevious}
                                            </Badge>
                                        </>
                                    )}
                                    {(groupHasWarnings || groupHasErrors) && (
                                        <>
                                            <span className="text-slate-400 dark:text-slate-500">•</span>
                                            <Badge
                                                variant="outline"
                                                className={`text-xs ${groupHasErrors ? 'bg-red-100 text-red-700 border-red-300' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}`}
                                            >
                                                {groupHasErrors ? '⚠️ Error' : '⚡ Warning'}
                                            </Badge>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Timeline items */}
                            <div className="space-y-1">
                                {group.map((log, logIndex) => {
                                    const previousLogTimestamp = logIndex > 0 ? group[logIndex - 1].createdAt : undefined;
                                    const isLastInGroup = logIndex === group.length - 1;
                                    const isFirstInGroup = logIndex === 0;
                                    const sendInfo = sendNumberMap.get(log._id);

                                    return (
                                        <TimelineItem
                                            key={log._id || `${messageId}-${groupIndex}-${logIndex}`}
                                            log={log}
                                            isLast={isLastInGroup}
                                            isFirst={isFirstInGroup}
                                            dueDate={dueDate}
                                            previousLogTimestamp={previousLogTimestamp}
                                            sendInfo={sendInfo}
                                        />
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}
